// Route Intelligence → Load matching (ST-00218 P2; SPEC §5 "UI"; API scratchpad/st00218/API.md).
//
// Compares delivery-number Brought Forward / Carried Forward matching ("now", utils/routeTotals)
// with the one load-matching engine ("new", the ledger) for a period and a set of routes, as a
// review and diagnostic tool. ST-00218 P3 (SPEC §10): a route's matching is its Load matching
// setting (Truck + time by default); sign-off is optional and gates nothing:
//   1. headline strip: adjusted dispatched, received, BF, CF, missing, receipts with no dispatch,
//      variance — now vs new — and the bridge in one line;
//   2. route table (cards on a phone) with batch selection → export for review / sign off;
//   3. the route drawer (changed loads, data quality with amend links, transit time + window,
//      sign-off / switch / switch back).
// GET /status first: LOAD_MATCHING=off (or a 503 from /compare) shows the switched-off state.
// load_matching.manage (status.permissions.manage) gates sign-off, switch, revert and the window
// override; load_matching.view users get everything read-only.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaFileCsv, FaFilePdf, FaInfoCircle, FaSignature, FaSyncAlt } from 'react-icons/fa';
import UnifiedDateRangePicker from '../../../common/UnifiedDateRangePicker';
import ThemedSelect from '../../../common/ThemedSelect';
import { usePhoneLayout } from '../../../common/phoneLayout';
import { formatRelativeTime, formatMiningDate, toApiDateString } from '../../../../utils/dateUtils';
import {
  downloadExport,
  getCompare,
  getStatus,
  isSwitchedOff,
  loadMatchingErrorText,
  type ExportFormat,
} from '../../../../services/loadMatching';
import type {
  CompareResponse,
  CompareRouteRow,
  LoadMatchingStatusResponse,
} from '../../../../types/loadMatching';
import HeadlineStrip from './HeadlineStrip';
import RouteDrawer from './RouteDrawer';
import RouteTable from './RouteTable';
import SignoffDialog from './SignoffDialog';
import { fmtN, fmtPeriod, isNa, lastFullMonth } from './format';
import { SwitchedOffState, TierLegend } from './parts';

/** Max routes per export / sign-off (API: groupingIds 1–20). */
export const MAX_BATCH = 20;

export interface LoadMatchingTabProps {
  /** Deep link (`/route-intelligence?tab=load-matching&startDate=…&endDate=…&groupingIds=…&route=…`). */
  initial?: {
    startDate?: string | null;
    endDate?: string | null;
    groupingIds?: number[];
    route?: number | null;
  };
}

type View = 'all' | 'changed' | 'action';

interface Applied {
  startDate: string;
  endDate: string;
  groupingIds: number[];
}

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** Does anything about the route's figures differ between the two matchings? */
export function routeChanges(r: CompareRouteRow): boolean {
  if (r.delta.bfN !== 0 || r.delta.cfN !== 0) return true;
  if (r.new.missingN > 0 || r.new.orphanN > 0 || r.new.receivedOffRouteN > 0) return true;
  if (isNa(r.new) !== (r.old.variancePct === null)) return true;
  const p = r.delta.variancePctPoints;
  return p !== null && Math.abs(p) >= 0.005;
}

export default function LoadMatchingTab({ initial }: LoadMatchingTabProps) {
  const phone = usePhoneLayout();
  const defaults = useMemo(() => lastFullMonth(), []);
  const [status, setStatus] = useState<LoadMatchingStatusResponse | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [off, setOff] = useState(false);

  const [startDate, setStartDate] = useState(
    initial?.startDate && YMD.test(initial.startDate) ? initial.startDate : defaults.startDate
  );
  const [endDate, setEndDate] = useState(
    initial?.endDate && YMD.test(initial.endDate) ? initial.endDate : defaults.endDate
  );
  const [routeFilter, setRouteFilter] = useState<number[]>(initial?.groupingIds ?? []);
  const [applied, setApplied] = useState<Applied | null>(null);
  const [data, setData] = useState<CompareResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [routeOptions, setRouteOptions] = useState<Map<number, string>>(new Map());
  const [view, setView] = useState<View>('all');
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [openRoute, setOpenRoute] = useState<number | null>(initial?.route ?? null);
  const [signoffFor, setSignoffFor] = useState<CompareRouteRow[] | null>(null);
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const canManage = !!status?.permissions.manage;

  const run = useCallback(async (params: Applied, quiet = false) => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    if (!quiet) setError(null);
    setApplied(params);
    try {
      const res = await getCompare(params, { signal: ac.signal });
      if (ac.signal.aborted) return;
      setData(res);
      setError(null);
      setRouteOptions((prev) => {
        const next = new Map(prev);
        for (const r of res.routes) next.set(r.groupingId, r.name);
        return next;
      });
      setChecked((prev) => {
        const ids = new Set(res.routes.map((r) => r.groupingId));
        return new Set([...prev].filter((id) => ids.has(id)));
      });
    } catch (err) {
      if (ac.signal.aborted) return;
      if (isSwitchedOff(err)) {
        setOff(true);
        return;
      }
      setError(loadMatchingErrorText(err, 'The comparison could not be loaded. Try again.'));
    } finally {
      if (!ac.signal.aborted) setLoading(false);
    }
  }, []);

  // Status first; then the first comparison with the initial period and routes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getStatus();
        if (cancelled) return;
        setStatus(s);
        if (s.mode === 'off') {
          setOff(true);
          return;
        }
        void run({ startDate, endDate, groupingIds: routeFilter });
      } catch (err) {
        if (cancelled) return;
        if (isSwitchedOff(err)) setOff(true);
        else setStatusError(loadMatchingErrorText(err, 'Load matching could not be reached.'));
      }
    })();
    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
    // Mount only: later runs come from the Compare button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(() => {
    if (applied) void run(applied, true);
  }, [applied, run]);

  const visible = useMemo(() => {
    const rows = data?.routes ?? [];
    if (view === 'changed') return rows.filter(routeChanges);
    if (view === 'action') return rows.filter((r) => r.dq.indicator === 'action');
    return rows;
  }, [data, view]);

  const toggle = (id: number) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = (ids: number[], on: boolean) =>
    setChecked((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  const selectedRows = (data?.routes ?? []).filter((r) => checked.has(r.groupingId));
  const tooMany = selectedRows.length > MAX_BATCH;

  const exportSelected = async (format: ExportFormat) => {
    if (!applied || !selectedRows.length || tooMany) return;
    setExporting(format);
    setNotice(null);
    try {
      await downloadExport({
        startDate: applied.startDate,
        endDate: applied.endDate,
        groupingIds: selectedRows.map((r) => r.groupingId),
        format,
      });
    } catch (err) {
      setNotice(loadMatchingErrorText(err, 'The document could not be downloaded.'));
    } finally {
      setExporting(null);
    }
  };

  // ── states before the figures ─────────────────────────────────────────────
  if (off) return <SwitchedOffState />;
  if (statusError) {
    return (
      <div
        role="alert"
        className="p-4 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700"
      >
        {statusError}
      </div>
    );
  }

  const ledger = data?.ledger ?? status?.ledger;
  const pendingN = data ? data.pending.dispatches + data.pending.receipts : 0;
  const beforeHorizon =
    !!ledger?.horizonStart && !!applied && applied.startDate < ledger.horizonStart.slice(0, 10);
  const optionIds = [...routeOptions.keys()];

  return (
    <div>
      <p className="text-xs text-slate-600 mb-3 max-w-3xl">
        Compare delivery-number Brought Forward / Carried Forward with the new load matching (Truck
        + time), route by route, and review the changed loads and the data-quality findings. A
        route&apos;s matching is its Load matching setting in Settings → Route Groupings. Signing
        off is optional: it records the review for the customer&apos;s file and gates nothing.
      </p>

      {status?.mode === 'shadow' && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
          <FaInfoCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span>
            Preview mode: this tab only compares. Pages show delivery-number figures until load
            matching is switched on for this server.
          </span>
        </div>
      )}

      {/* One filter row above everything it scopes */}
      <div className="bg-surface rounded-lg border border-slate-200 px-3 sm:px-4 py-3 mb-4 flex flex-wrap items-end gap-x-4 gap-y-3">
        <div className="w-full sm:w-auto">
          <span className="block text-[11px] font-medium text-slate-500 mb-1">Period</span>
          <div className="w-full sm:w-[264px]">
            <UnifiedDateRangePicker
              startDate={new Date(`${startDate}T00:00:00`)}
              endDate={new Date(`${endDate}T00:00:00`)}
              onStartDateChange={(d) => d && setStartDate(toApiDateString(d))}
              onEndDateChange={(d) => d && setEndDate(toApiDateString(d))}
              disabled={loading}
            />
          </div>
        </div>
        <div className="flex-1 min-w-full sm:min-w-[280px]">
          <span className="block text-[11px] font-medium text-slate-500 mb-1">Routes</span>
          <ThemedSelect
            isMulti
            value={routeFilter}
            onChange={(e) => {
              const v = e.target.value;
              setRouteFilter((Array.isArray(v) ? v : []).map(Number).filter((n) => n > 0));
            }}
            placeholder={optionIds.length ? 'All active routes' : 'Loading routes…'}
            disabled={loading && !data}
            aria-label="Routes"
            className="w-full"
          >
            {optionIds.map((id) => (
              <option key={id} value={id}>
                {routeOptions.get(id)}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div className="w-full sm:w-auto">
          <button
            type="button"
            onClick={() => void run({ startDate, endDate, groupingIds: routeFilter })}
            disabled={loading || !status}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-9 px-5 rounded-lg text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:bg-slate-200 disabled:text-slate-400"
          >
            <FaSyncAlt className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            {loading ? 'Comparing…' : 'Compare'}
          </button>
        </div>
      </div>

      {ledger && (
        <p className="text-[11px] text-slate-500 mb-3">
          {ledger.watermark
            ? `Matching last updated ${formatRelativeTime(ledger.watermark)}`
            : 'The first matching run has not finished yet — check back in a few minutes.'}
          {status && ` · engine ${status.engineVersion}`}
          {applied && ` · ${fmtPeriod(applied)}`}
        </p>
      )}
      {pendingN > 0 && (
        <p className="mb-3 text-xs text-amber-800">
          {fmtN(pendingN)} tickets in this period are not matched yet (the matching job is catching
          up). They count as in transit for now.
        </p>
      )}
      {beforeHorizon && ledger?.horizonStart && (
        <p className="mb-3 text-xs text-amber-800">
          Matching history starts on {formatMiningDate(ledger.horizonStart)}. Loads before that show
          as not matched yet.
        </p>
      )}

      {error && (
        <div
          role="alert"
          className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700"
        >
          {error}{' '}
          {applied && (
            <button type="button" className="underline" onClick={() => void run(applied)}>
              Retry
            </button>
          )}
        </div>
      )}

      {!data && loading && (
        <div className="py-16 text-center" role="status">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-primary-500" />
          <p className="mt-3 text-xs text-slate-500">
            Comparing the two matchings for every route. A month takes a few seconds; a year can
            take a minute.
          </p>
        </div>
      )}

      {data && (
        <div className={loading ? 'opacity-60 transition-opacity pointer-events-none' : ''}>
          <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
            <h3 className="text-sm font-semibold text-slate-900">
              {data.headline.groupingIds.length
                ? `${fmtN(data.headline.groupingIds.length)} routes with dispatches and receipts`
                : 'No route has both dispatches and receipts in this period'}
            </h3>
            <span className="text-[11px] text-slate-500">
              Big figures: Truck + time · “Now”: delivery-number matching
            </span>
          </div>
          {data.headline.groupingIds.length > 0 && <HeadlineStrip headline={data.headline} />}

          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div
              className="flex gap-0.5 bg-slate-100 rounded-lg p-0.5"
              role="group"
              aria-label="Show routes"
            >
              {(
                [
                  ['all', `All ${fmtN(data.routes.length)}`],
                  ['changed', `Figures change ${fmtN(data.routes.filter(routeChanges).length)}`],
                  [
                    'action',
                    `Action needed ${fmtN(data.routes.filter((r) => r.dq.indicator === 'action').length)}`,
                  ],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={view === id}
                  onClick={() => setView(id)}
                  className={`px-2.5 py-1 text-[11px] font-semibold rounded-md whitespace-nowrap ${
                    view === id
                      ? 'bg-surface text-slate-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <TierLegend />
          </div>

          {selectedRows.length > 0 && (
            <div
              className="sticky top-0 z-10 mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2"
              data-testid="selection-bar"
            >
              <span className="text-xs font-semibold text-primary-900">
                {selectedRows.length === 1
                  ? '1 route selected'
                  : `${fmtN(selectedRows.length)} routes selected`}
              </span>
              <span className="flex-1" />
              {tooMany ? (
                <span className="text-[11px] text-amber-800">
                  Select at most {MAX_BATCH} routes to export or sign off.
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={exporting !== null}
                    onClick={() => void exportSelected('pdf')}
                    className="inline-flex items-center gap-1 h-8 px-2.5 text-[11px] font-medium rounded-md border border-slate-300 bg-surface text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    title="The document for review: figures, changed loads and the transit window of each selected route"
                  >
                    <FaFilePdf className="w-3 h-3" />{' '}
                    {exporting === 'pdf' ? 'Preparing…' : 'Export PDF'}
                  </button>
                  <button
                    type="button"
                    disabled={exporting !== null}
                    onClick={() => void exportSelected('csv')}
                    className="inline-flex items-center gap-1 h-8 px-2.5 text-[11px] font-medium rounded-md border border-slate-300 bg-surface text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <FaFileCsv className="w-3 h-3" />{' '}
                    {exporting === 'csv' ? 'Preparing…' : 'Export CSV'}
                  </button>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => setSignoffFor(selectedRows)}
                      className="inline-flex items-center gap-1 h-8 px-3 text-[11px] font-semibold rounded-md bg-primary-600 text-white hover:bg-primary-700"
                    >
                      <FaSignature className="w-3 h-3" /> Sign off selected
                    </button>
                  )}
                </>
              )}
              <button
                type="button"
                onClick={() => setChecked(new Set())}
                className="h-8 px-2 text-[11px] font-medium text-slate-600 hover:text-slate-900"
              >
                Clear
              </button>
            </div>
          )}
          {notice && <p className="mb-2 text-xs text-red-700">{notice}</p>}

          {visible.length > 0 ? (
            <RouteTable
              routes={visible}
              checked={checked}
              onToggle={toggle}
              onToggleAll={toggleAll}
              onOpen={setOpenRoute}
              phone={phone}
            />
          ) : (
            <p className="py-10 text-center text-xs text-slate-500">
              {data.routes.length
                ? 'No route matches this filter.'
                : 'No route has loads in this period.'}
            </p>
          )}
          {data.emptyRouteIds.length > 0 && (
            <p className="mt-2 text-[11px] text-slate-500">
              {fmtN(data.emptyRouteIds.length)} selected route
              {data.emptyRouteIds.length === 1 ? '' : 's'} had no loads in this period.
            </p>
          )}
        </div>
      )}

      {openRoute !== null && applied && status && status.mode !== 'off' && (
        <RouteDrawer
          groupingId={openRoute}
          period={{ startDate: applied.startDate, endDate: applied.endDate }}
          mode={status.mode}
          canManage={canManage}
          semanticChanges={data?.semanticChanges ?? []}
          onClose={() => setOpenRoute(null)}
          onChanged={refresh}
        />
      )}
      {signoffFor && applied && (
        <SignoffDialog
          routes={signoffFor.map((r) => ({ groupingId: r.groupingId, name: r.name }))}
          period={{ startDate: applied.startDate, endDate: applied.endDate }}
          semanticChanges={data?.semanticChanges ?? []}
          onClose={() => setSignoffFor(null)}
          onSigned={() => {
            setChecked(new Set());
            refresh();
          }}
        />
      )}
    </div>
  );
}
